import {
  CashInfo,
  Currency,
  getFormatter,
  InstrumentCategoryEnum,
  isDefined,
  ListedInstrument,
  Loadable,
  LoadableState,
  useDictionary,
} from '@eih-netbroker/common';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { ReactComponent as ArrowDown } from '../../../../assets/icons/arrow-down-s-line.svg';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { NbIndefiniteLoader } from '../../../../shared/components/loader/Loader';
import { MoneyMaskHOC } from '../../../../shared/components/money-mask/MoneyMask';
import { useAccountBalance } from '../../../../shared/services/useAccountBalance';
import { useAccounts } from '../../../../shared/services/useAccounts';
import { useTradingClassification } from '../../../../shared/services/useTradingClassification';
import { cssWhen } from '../../../../shared/utils/themeUtils';
import { useLogger } from '../../../../shared/utils/useLogger';
import { useCreateNormalOrder } from '../../services/order/useNormalOrderView';
import { useOrderManagement } from '../../services/order/useOrderManagement';
import { useOrderState } from '../OrderStateContext';
import { FuturesMaxPosition } from './FuturesMaxPosition';
import classes from './OrderTransactionInfoAccordion.module.scss';

export interface OrderTransactionInfoProps {
  instrument?: ListedInstrument | null;
  className?: string;
}

const unknownValue = '-';

export const OrderTransactionInfo = ({ instrument, className }: OrderTransactionInfoProps) => {
  const logger = useLogger(`OrderTransactionInfo`);
  const { selectedAccountOrClient } = useAccounts();
  const { formatDictionaryType } = useDictionary();
  const { translation } = useLocale();
  const { formatCurrency } = getFormatter();
  const { getCashInfoForCurrency } = useAccountBalance();
  const [cashInfo, setCashInfo] = useState<LoadableState<CashInfo>>(Loadable.uninitialized);
  const [maxPosition, setMaxPosition] = useState<number | undefined>();
  const { tradingClassification } = useTradingClassification();
  const { getMaxOpenPosition } = useOrderManagement();
  const { getAvailableBalance } = useCreateNormalOrder();
  const { availableBalance, setAvailableBalance } = useOrderState();

  const calculateMaxPosition = useCallback(
    async (instrument: ListedInstrument) => {
      try {
        const maxOpenPosition = await getMaxOpenPosition(instrument);
        setMaxPosition(maxOpenPosition.data.maximumPosition);
      } catch (err) {
        logger.error('Failed to get max open position', err);
      }
    },
    [logger, getMaxOpenPosition],
  );

  useEffect(() => {
    if (!isDefined(selectedAccountOrClient.account)) {
      setAvailableBalance(Loadable.uninitialized);
      return;
    }

    if (instrument) {
      getAvailableBalance(instrument);
    }
  }, [getAvailableBalance, instrument, selectedAccountOrClient.account, setAvailableBalance]);

  useEffect(() => {
    setCashInfo(Loadable.loading);
    if (selectedAccountOrClient.account?.accountNumber && instrument) {
      if (
        tradingClassification?.hasInvestmentLoanContract &&
        isDefined(instrument.collateralValue) &&
        instrument.collateralValue < 1 &&
        instrument.collateralValue > 0 &&
        instrument.instrumentCategory !== InstrumentCategoryEnum.Futures // handle futures in a separate component
      ) {
        calculateMaxPosition(instrument);
      }
      const accountCashInfo = getCashInfoForCurrency(selectedAccountOrClient, instrument.currency as Currency);

      if (accountCashInfo.isLoaded) {
        setCashInfo(accountCashInfo);
      }
    } else {
      setCashInfo(Loadable.uninitialized);
    }
  }, [
    selectedAccountOrClient,
    instrument,
    getCashInfoForCurrency,
    calculateMaxPosition,
    tradingClassification?.hasInvestmentLoanContract,
  ]);

  return (
    <Accordion
      className={cssWhen(classes.transactionInfo, className)}
      style={{ boxShadow: 'none' }}
      disableGutters
    >
      <AccordionSummary
        className={classes.transactionInfoSummary}
        data-testid="order-transaction-info-button"
        expandIcon={<ArrowDown />}
      >
        {translation.orderModal.transactionInfo.title}
      </AccordionSummary>
      <AccordionDetails>
        <span>{translation.orderModal.transactionInfo.debitAccount}</span>
        <span>
          {selectedAccountOrClient.account?.accountCategory
            ? formatDictionaryType(selectedAccountOrClient.account.accountCategory, 'accountCategoryTypes')
            : unknownValue}
        </span>
      </AccordionDetails>
      {tradingClassification?.hasInvestmentLoanContract &&
        instrument?.instrumentCategory !== InstrumentCategoryEnum.Futures && (
          <AccordionDetails>
            <span>{translation.orderModal.transactionInfo.maxPosition}</span>
            <MoneyMaskHOC
              isInverse
              size={100}
            >
              {(cashInfo.isLoading || availableBalance.isLoading) && (
                <NbIndefiniteLoader
                  isContrast
                  size={100}
                />
              )}
              {cashInfo.isLoaded && availableBalance.isLoaded && (
                <span className={classes.number}>
                  {maxPosition && cashInfo ? formatCurrency(maxPosition, cashInfo?.ccy) : unknownValue}
                </span>
              )}
            </MoneyMaskHOC>
          </AccordionDetails>
        )}
      {instrument?.instrumentCategory === InstrumentCategoryEnum.Futures && (
        <FuturesMaxPosition
          instrument={instrument}
          selectedAccountOrClient={selectedAccountOrClient}
        />
      )}
      <AccordionDetails>
        <span>{translation.orderModal.transactionInfo.currencyBalance}</span>
        <MoneyMaskHOC
          isInverse
          size={100}
        >
          {(cashInfo.hasError ||
            Loadable.isUninitialized(cashInfo) ||
            availableBalance.hasError ||
            Loadable.isUninitialized(availableBalance)) && <span className={classes.number}>{unknownValue}</span>}
          {(cashInfo.isLoading || availableBalance.isLoading) && (
            <NbIndefiniteLoader
              isContrast
              size={100}
            />
          )}
          {cashInfo.isLoaded && availableBalance.isLoaded && (
            <span className={classes.number}>{formatCurrency(availableBalance.value, cashInfo.ccy)}</span>
          )}
        </MoneyMaskHOC>
      </AccordionDetails>
    </Accordion>
  );
};
